import { z } from 'zod';

/**
 * Bağlı test ortamı hesapları (ADR 0092; sahip kararı 2026-10-20): tanıtım şirketinde
 * (`FELIX_DEMO_TENANT`) her rütbenin ve her ofis rolünün kişi hesabı, tek ortak test parolasıyla.
 * Bulutun `GET /api/demo/test-accounts` ucunun biçimi; gemi uygulaması ve web ofis giriş ekranı
 * aynı şemayla okur. Söküm: bu dosya ve dışa aktarımı (ADR 0092 "Sökme").
 */

export const testAccountSchema = z.object({
  email: z.string().min(3).max(320),
  fullName: z.string().min(1).max(200),
  /** Rütbe (gemi ataması rolü) ya da ofis rolü; ekranda olduğu gibi yazılır. */
  role: z.string().min(1).max(200),
});

export const testAccountsSchema = z.object({
  company: z.object({ slug: z.string(), name: z.string() }),
  /** Ortak test parolası: sunucu ayarından (`FELIX_TEST_ACCOUNTS_PASSWORD`), açıkça gösterilir. */
  password: z.string().min(1).max(1024),
  office: z.array(testAccountSchema),
  vessels: z.array(
    z.object({ id: z.string(), name: z.string(), crew: z.array(testAccountSchema) }),
  ),
});

export type TestAccount = z.infer<typeof testAccountSchema>;
export type TestAccounts = z.infer<typeof testAccountsSchema>;

/** Ortak test parolasının en kısa uzunluğu: ayar bundan kısaysa özellik açılmaz. */
export const TEST_ACCOUNTS_PASSWORD_MIN = 10;

/**
 * Gemide rütbe sırası (koordinatör, 2026-10-20): Master, C/E, C/O, 2/E, 2/O, 3/E, 3/O…, sonra
 * tayfa; tanıtım yöneticisi (rütbe değildir) en sonda.
 */
const RANK_ORDER = [
  'Master',
  'Chief Engineer',
  'Chief Officer',
  '2nd Engineer',
  'Second Engineer',
  '2nd Officer',
  'Second Officer',
  '3rd Engineer',
  'Third Engineer',
  '3rd Officer',
  'Third Officer',
  'Deck Officer',
  'Engineer Officer',
  'Electrician',
  'ETO',
  'Bosun',
  'AB',
  'OS',
  'Oiler',
  // Chief Cook / Chief Steward rütbeleri kaldırıldı; kumanyayı birinci zabit yürütür (ADR 0139)
  'Steward',
  'Cook',
  'Cleaner',
  'Demo Administrator',
];

/**
 * Ofiste rol sırası: yönetici, ofis yöneticisi, süperintendent, DPA; sonra ofis ünvanları (ADR
 * 0139 §3: teknik, güverte, operasyon, personel); ötekiler adına göre.
 */
const OFFICE_ORDER = [
  'Company Administrator',
  'Office Manager',
  'Superintendent',
  'Designated Person Ashore',
  'Technical Manager',
  'Technical Superintendent',
  'Marine Superintendent',
  'Deck Superintendent',
  'Operation Manager',
  'Crew Manager',
  'Assistant Crew Manager',
];

function rankOf(order: readonly string[], role: string): number {
  const index = order.findIndex((name) => name.toLowerCase() === role.toLowerCase());
  return index === -1 ? order.length : index;
}

/** Bir kişinin birden çok rolünden ekranda gösterileni: sırada önde olan. */
export function preferredRole(roles: readonly string[], at: 'vessel' | 'office'): string | null {
  const order = at === 'vessel' ? RANK_ORDER : OFFICE_ORDER;
  return (
    [...roles].sort((a, b) => rankOf(order, a) - rankOf(order, b) || a.localeCompare(b, 'en'))[0] ??
    null
  );
}

/** Ekrandaki sıra: rütbe ya da rol sırası, sonra ad. */
export function sortTestAccounts<T extends TestAccount>(
  accounts: readonly T[],
  at: 'vessel' | 'office',
): T[] {
  const order = at === 'vessel' ? RANK_ORDER : OFFICE_ORDER;
  return [...accounts].sort(
    (a, b) =>
      rankOf(order, a.role) - rankOf(order, b.role) ||
      a.role.localeCompare(b.role, 'en') ||
      a.fullName.localeCompare(b.fullName, 'tr'),
  );
}
